// JSX port of the companion's renderWeightChart + renderCategoryDoughnut. Faithful
// react-chartjs-2 replica of the phone's weight doughnut. Series built from
// useMeasureReadings via buildWeightSeries/WEIGHT_TYPES (weightReadings.ts), scalars
// from the view props. Weight DISPLAYS in lb (companion parity — US program);
// classification math stays kg (ratios vs the kg baseline).
//
// NOTE (deliberate deviation): the doughnut bucketing here is the spark's own
// proximity-to-BASELINE classification (above / flat / progressing / at_goal). It is
// NOT logic/patientHome.classifyWeight — that helper buckets by proximity-to-TARGET
// (onTarget/near/above), which is a different, incompatible shape. Weight bucketing
// is cosmetic (not an s04-governed clinical threshold), so the spark's version is
// ported inline for a faithful chart.

import React, { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { useMeasureReadings } from '@/state/data/patient';
import { measureWindowYmd } from '@/logic/measureWindow';
import { buildWeightSeries, WEIGHT_TYPES, WEIGHT_MEASTYPES } from '@/logic/weightReadings';
import { getWeightTargetLb, kgToLb, num, trendArrow, trendLabel } from '@/logic/patientHome';

ChartJS.register(ArcElement, Tooltip, Legend);

const WEIGHT_CLS = {
  above: { color: '#DC2626', label: 'Above baseline' },
  flat: { color: '#9CA3AF', label: 'Flat (±1%)' },
  progressing: { color: '#D97706', label: 'Progressing' },
  at_goal: { color: '#22C55E', label: 'At goal (-5% +)' },
};
const WEIGHT_ORDER = ['above', 'flat', 'progressing', 'at_goal'];

function classifyWeightVsBaseline(weight, baseline) {
  if (!baseline || baseline <= 0) return 'flat';
  const pct = ((weight - baseline) / baseline) * 100;
  if (pct > 1) return 'above';
  if (pct >= -1) return 'flat';
  if (pct > -5) return 'progressing';
  return 'at_goal';
}

const CHART_TOOLTIP = {
  backgroundColor: '#2D2B28',
  titleColor: '#FFFFFF',
  bodyColor: '#DDD9D5',
  borderColor: 'rgba(255,255,255,0.1)',
  borderWidth: 1,
  padding: 10,
  titleFont: { family: 'Aeonik', size: 14 },
  bodyFont: { family: 'Aeonik', size: 15 },
  displayColors: true,
  boxWidth: 8,
  boxHeight: 8,
};

function SectionShell({ trend, children }) {
  return (
    <section className="acc-section fade-up" aria-labelledby="ph-weight-title">
      <div className="acc-section__header">
        <h2 className="acc-section__title" id="ph-weight-title">
          Weight
          {trend && <span className={`acc-section__badge acc-section__badge--${trend}`}>{trendArrow(trend)} {trendLabel(trend)}</span>}
        </h2>
      </div>
      <div>{children}</div>
    </section>
  );
}

export default function WeightChart({ userid, carePlan, measureStates }) {
  const { startdateymd, enddateymd } = useMemo(() => measureWindowYmd(30), []);
  const query = useMeasureReadings(userid, WEIGHT_MEASTYPES, { startdateymd, enddateymd });
  const rawReadings = query.data?.readings ?? null;
  const history = useMemo(() => {
    if (!rawReadings) return [];
    const series = buildWeightSeries(rawReadings);
    return (series[WEIGHT_TYPES.weight] || []).map((p) => ({ epoch: p.epoch, weight: p.value }));
  }, [rawReadings]);

  const states = measureStates || [];
  const wt = states.find((m) => m.measure_type === 'weight');
  const trend = wt?.trend_direction || null;

  if (query.isLoading) {
    return <SectionShell trend={trend}><div className="acc-chart-card" style={{ padding: '20px 16px', textAlign: 'center' }}><p style={{ fontSize: 15 }}>Loading readings…</p></div></SectionShell>;
  }
  if (history.length === 0) {
    return (
      <SectionShell trend={trend}>
        <div className="acc-chart-card" style={{ padding: '20px 16px', textAlign: 'center' }}>
          <p className="acc-empty-note">Waiting for the first weigh-in</p>
          <p style={{ fontSize: 14, margin: '6px 0 0', color: 'var(--text-dim50)' }}>The Body Pro feeds this once used</p>
        </div>
      </SectionShell>
    );
  }

  const first = history[0];
  const latest = history[history.length - 1];
  const avgWeightLb = kgToLb(history.reduce((s, r) => s + r.weight, 0) / history.length).toFixed(1);
  // A comparison reference for the buckets below — NOT a baseline to display. Only a CMS-filed
  // baseline earns that word, and WF37 stamps 'reportable' when it files one (BpChart's gate).
  const baselineWeight = num(wt?.baseline_value) != null ? num(wt.baseline_value) : first.weight;
  const wtLocked = String(wt?.status) === 'reportable' && num(wt?.baseline_value) != null;
  const targetLb = getWeightTargetLb(carePlan?.goals, carePlan?.targets);

  const buckets = { above: 0, flat: 0, progressing: 0, at_goal: 0 };
  history.forEach((r) => { buckets[classifyWeightVsBaseline(r.weight, baselineWeight)] += 1; });
  const active = WEIGHT_ORDER.filter((k) => buckets[k] > 0);
  const total = active.reduce((s, k) => s + buckets[k], 0);

  const data = {
    labels: active.map((k) => WEIGHT_CLS[k].label),
    datasets: [{
      data: active.map((k) => buckets[k]),
      backgroundColor: active.map((k) => WEIGHT_CLS[k].color),
      borderColor: 'rgba(255,255,255,0.08)',
      borderWidth: 2,
      hoverOffset: 4,
    }],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: { display: false },
      tooltip: { ...CHART_TOOLTIP, callbacks: { label: (item) => ` ${item.label}: ${item.raw} (${Math.round((item.raw / total) * 100)}%)` } },
    },
  };

  return (
    <SectionShell trend={trend}>
      <div className="acc-chart-card">
        <div className="acc-chart-card__header">
          <div>
            <div className="acc-chart-card__value">{avgWeightLb}<span className="acc-chart-card__unit">lb</span></div>
            <div className="acc-chart-card__delta acc-chart-card__delta--neutral">30-day average</div>
          </div>
        </div>
        <div className="acc-chart-card__doughnut-row">
          <div className="acc-chart-card__doughnut">
            <Doughnut data={data} options={options} />
            <div className="acc-chart-card__doughnut-center">
              <span className="acc-chart-card__doughnut-count">{history.length}</span>
              <span className="acc-chart-card__doughnut-caption">readings</span>
            </div>
          </div>
          <div className="acc-chart-card__legend">
            {active.map((k) => (
              <div key={k} className="acc-chart-card__legend-row">
                <span className="acc-chart-card__legend-dot" style={{ background: WEIGHT_CLS[k].color }} />
                <span className="acc-chart-card__legend-count">{buckets[k]}</span>
                <span className="acc-chart-card__legend-label">{WEIGHT_CLS[k].label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="acc-chart-card__comparison">
          <div className="acc-chart-card__compare-item">
            <div className="acc-chart-card__compare-label">Baseline</div>
            {wtLocked ? (
              <div className="acc-chart-card__compare-value">{kgToLb(num(wt.baseline_value)).toFixed(1)} lb</div>
            ) : (
              <>
                <div className="acc-chart-card__compare-value acc-chart-card__compare-value--pending">Establishing</div>
                <div className="acc-chart-card__compare-sub">Awaiting care team</div>
              </>
            )}
          </div>
          <div className="acc-chart-card__compare-item">
            <div className="acc-chart-card__compare-label">Latest</div>
            <div className="acc-chart-card__compare-value">{kgToLb(latest.weight).toFixed(1)} lb</div>
          </div>
          {targetLb != null && (
            <div className="acc-chart-card__compare-item">
              <div className="acc-chart-card__compare-label">Target</div>
              <div className="acc-chart-card__compare-value">&lt;{targetLb.toFixed(1)} lb</div>
            </div>
          )}
        </div>
      </div>
    </SectionShell>
  );
}
